import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import Icon from '../../components/AppIcon';

import ReportHeader from './components/ReportHeader';
import ComplianceSummary from './components/ComplianceSummary';
import DocumentStatusSection from './components/DocumentStatusSection';
import { siteService } from '../../services/siteService';
import { documentService } from '../../services/documentService';
import { reportService } from '../../services/reportService';
import { pdfExportService } from '../../services/pdfExportService';
import { companyService } from '../../services/companyService';
import { useAuth } from '../../contexts/AuthContext';
import { getDefaultDashboard } from '../../utils/roleAccess';
import { useLanguage } from '../../contexts/LanguageContext';

const ComplianceReport = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [selectedSiteId, setSelectedSiteId] = useState('');
  const [sites, setSites] = useState([]);
  const [siteData, setSiteData] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [exportLoading, setExportLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [companyName, setCompanyName] = useState('');
  const { userProfile } = useAuth();

  useEffect(() => {
    loadSites();
    loadCompany();
  }, []);

  useEffect(() => {
    if (selectedSiteId) {
      loadSiteReport(selectedSiteId);
    }
  }, [selectedSiteId]);

  const loadSites = async () => {
    try {
      const result = await siteService?.getAllSites();
      if (result?.error) throw result.error;
      setSites(result?.data || []);
    } catch (err) {
      console.error('[ComplianceReport] load data failed:', err);
      setError('Failed to load compliance data. Please refresh the page.');
    }
  };

  const loadCompany = async () => {
    try {
      const result = await companyService?.getUserCompany();
      if (result?.data) setCompanyName(result?.data?.company_name || result?.data?.name || '');
    } catch {}
  };

  const loadSiteReport = async (siteId) => {
    try {
      setLoading(true);
      setError('');
      
      const [siteResult, documentsResult] = await Promise.all([
        siteService?.getSiteById(siteId),
        documentService?.getDocumentsBySite(siteId)
      ]);

      if (siteResult?.error) throw siteResult.error;
      if (documentsResult?.error) throw documentsResult.error;

      setSiteData(siteResult?.data);
      setDocuments(documentsResult?.data || []);
    } catch (err) {
      console.error('[ComplianceReport] load materials failed:', err);
      setError('Failed to load compliance data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const calculateComplianceData = () => {
    if (!documents || documents?.length === 0) {
      return {
        overallStatus: 'compliant',
        totalDocuments: 0,
        compliantDocs: [],
        expiringDocs: [],
        expiredDocs: [],
        compliancePercentage: 0
      };
    }

    const compliantDocs = documents?.filter(doc => doc?.status === 'valid');
    const expiringDocs = documents?.filter(doc => 
      doc?.status === 'expiring-soon' || doc?.status === 'expiring-critical'
    );
    const expiredDocs = documents?.filter(doc => doc?.status === 'expired');

    const compliancePercentage = Math.round((compliantDocs?.length / documents?.length) * 100);

    let overallStatus = 'compliant';
    if (expiredDocs?.length > 0 || expiringDocs?.filter(d => d?.status === 'expiring-critical')?.length > 0) {
      overallStatus = 'critical';
    } else if (expiringDocs?.length > 0) {
      overallStatus = 'warning';
    }

    return {
      overallStatus,
      totalDocuments: documents?.length,
      compliantDocs,
      expiringDocs,
      expiredDocs,
      compliancePercentage
    };
  };

  const handleExportPDF = async () => {
    if (!selectedSiteId || !siteData) {
      showToast('Please select a site first.', 'error');
      return;
    }
    try {
      setExportLoading(true);
      const reportResult = await reportService?.fetchReportData({
        siteId: selectedSiteId,
        companyId: userProfile?.company_id,
        modules: ['tasks', 'incidents', 'snags', 'documents', 'certificates'],
      });
      if (reportResult?.error) throw reportResult.error;
      const result = await pdfExportService?.generateComplianceReport({
        reportData: reportResult?.data,
        siteName: siteData?.site_name,
        companyName,
        reportType: 'compliance',
        selectedModules: ['tasks', 'incidents', 'snags', 'documents'],
      });
      if (result?.error) throw result.error;
      showToast(`PDF exported: ${result?.filename}`, 'success');
    } catch (err) {
      console.error('[ComplianceReport] export PDF failed:', err);
      showToast('Failed to export PDF. Please try again.', 'error');
    } finally {
      setExportLoading(false);
    }
  };

  const handleExportCSV = () => {
    if (!documents || documents?.length === 0) return;

    const headers = ['Document Title', 'Category', 'Status', 'Issue Date', 'Expiry Date', 'Days Until Expiry', 'Owner'];
    const rows = documents?.map(doc => [
      doc?.title || '',
      doc?.category || '',
      doc?.status || '',
      doc?.issue_date ? new Date(doc?.issue_date)?.toLocaleDateString('en-GB') : '',
      doc?.expiry_date ? new Date(doc?.expiry_date)?.toLocaleDateString('en-GB') : '',
      doc?.days_until_expiry || '',
      doc?.owner || ''
    ]);

    const csvContent = [
      headers?.join(','),
      ...rows?.map(row => row?.map(cell => `"${cell}"`)?.join(','))
    ]?.join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link?.setAttribute('href', url);
    link?.setAttribute('download', `compliance-report-${siteData?.site_name || 'site'}-${new Date()?.toISOString()?.split('T')?.[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body?.appendChild(link);
    link?.click();
    document.body?.removeChild(link);
  };

  const siteOptions = sites?.map(site => ({
    value: site?.id,
    label: site?.site_name
  }));

  const complianceData = calculateComplianceData();

  const breadcrumbItems = [
    { label: 'Dashboard', href: getDefaultDashboard(userProfile?.role) },
    { label: 'Compliance Report' }
  ];

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  return (
    <>
      <Helmet>
        <title>Compliance Report - SiteProof</title>
      </Helmet>
      
      <div className="min-h-screen bg-background print:bg-white">
        <Header />
        
        <main className="pt-20 pb-8 px-4 lg:px-6 max-w-7xl mx-auto">
          {/* Toast notification */}
          {toast && (
            <div className={`fixed top-6 right-6 z-50 flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg text-sm font-medium transition-all ${
              toast?.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
            }`}>
              <Icon name={toast?.type === 'success' ? 'CheckCircle2' : 'AlertCircle'} size={16} />
              {toast?.message}
            </div>
          )}

          <div className="no-print mb-6">
            <Breadcrumb items={breadcrumbItems} />
          </div>

          <div className="no-print mb-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">{t('complianceReport')}</h1>
                <p className="text-sm text-muted-foreground">{t('complianceReportSubtitle')}</p>
              </div>
              
              {selectedSiteId && (
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    iconName={exportLoading ? 'Loader2' : 'Download'}
                    onClick={handleExportPDF}
                    disabled={exportLoading}
                    title={t('exportAsPdf')}
                  >
                    {exportLoading ? t('exporting') : t('exportPdf')}
                  </Button>
                  <Button
                    variant="outline"
                    iconName="FileSpreadsheet"
                    onClick={handleExportCSV}
                    title={t('exportAsCsv')}
                  >
                    {t('exportCsv')}
                  </Button>
                </div>
              )}
            </div>
          </div>

          <div className="no-print mb-6">
            <div className="bg-card border border-border rounded-lg p-4 md:p-6 shadow-elevation-2">
              <label className="block text-sm font-medium text-foreground mb-2">
                {t('selectSite')}
              </label>
              <Select
                options={siteOptions}
                value={selectedSiteId}
                onChange={setSelectedSiteId}
                placeholder={t('chooseSiteToGenerateReport')}
                searchable
                className="max-w-md"
              />
            </div>
          </div>

          {error && (
            <div className="mb-6 bg-error/10 border border-error/20 rounded-lg p-4 flex items-start gap-3">
              <Icon name="AlertCircle" size={20} className="text-error flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-error">{t('errorLoadingReport')}</p>
                <p className="text-sm text-error/80 mt-1">{error}</p>
              </div>
            </div>
          )}

          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent"></div>
            </div>
          )}

          {!loading && selectedSiteId && siteData && (
            <div className="space-y-6">
              <ReportHeader siteData={siteData} />
              
              <ComplianceSummary 
                status={complianceData?.overallStatus}
                totalDocuments={complianceData?.totalDocuments}
                compliancePercentage={complianceData?.compliancePercentage}
                compliantCount={complianceData?.compliantDocs?.length}
                expiringCount={complianceData?.expiringDocs?.length}
                expiredCount={complianceData?.expiredDocs?.length}
              />

              <DocumentStatusSection 
                title="Compliant Documents"
                documents={complianceData?.compliantDocs}
                statusType="compliant"
              />

              <DocumentStatusSection 
                title="Expiring Documents"
                documents={complianceData?.expiringDocs}
                statusType="expiring"
              />

              <DocumentStatusSection 
                title="Missing or Expired Documents"
                documents={complianceData?.expiredDocs}
                statusType="expired"
              />
            </div>
          )}

          {!loading && !selectedSiteId && (
            <div className="bg-card border border-border rounded-lg p-8 md:p-12 text-center">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center">
                  <Icon name="FileText" size={32} className="text-muted-foreground" />
                </div>
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">{t('noSiteSelected')}</h3>
              <p className="text-sm text-muted-foreground max-w-md mx-auto">
                {t('selectSiteToGenerateReport')}
              </p>
            </div>
          )}
        </main>
      </div>
    </>
  );
};

export default ComplianceReport;